import React, { useState, useMemo } from 'react';
import {
    Paper,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    TextField,
    InputAdornment,
    Button,
    Chip,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    Search,
    FileDownload,
    Receipt,
    CheckCircle,
    HourglassTop,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
    formatCurrency,
    formatKwh,
    formatBillingPeriod,
    exportPowerCostToCsv,
} from '../../utils/powerCostUtils';
import { getMonthName } from '../../utils/monthlyDataUtils';

function PowerCostBreakdownTable({
    records = [],
    selectedYear,
    onSelectMonth,
    selectedMonth,
}) {
    const { t } = useTranslation();

    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [orderBy, setOrderBy] = useState('month');
    const [order, setOrder] = useState('desc');

    const handleSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setPage(0);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleExport = () => {
        exportPowerCostToCsv(filteredData, selectedYear, t);
    };

    // Filter by search term (Month name or status)
    const filteredData = useMemo(() => {
        if (!searchTerm.trim()) return records;
        const lower = searchTerm.toLowerCase();
        return records.filter((item) => {
            const mName = getMonthName(item.month, t).toLowerCase();
            const statusText = item.isPartial ? 'partial in progress' : 'completed complete';
            return mName.includes(lower) || statusText.includes(lower);
        });
    }, [records, searchTerm, t]);

    // Sort data
    const sortedData = useMemo(() => {
        return [...filteredData].sort((a, b) => {
            let aVal = a[orderBy];
            let bVal = b[orderBy];

            if (orderBy === 'month') {
                aVal = a.month || 0;
                bVal = b.month || 0;
            } else if (orderBy === 'totalCost' || orderBy === 'totalKwh' || orderBy === 'baseEnergyCost' || orderBy === 'ftCost') {
                aVal = parseFloat(a[orderBy]) || 0;
                bVal = parseFloat(b[orderBy]) || 0;
            }

            if (typeof aVal === 'string') {
                return order === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            }
            return order === 'asc' ? aVal - bVal : bVal - aVal;
        });
    }, [filteredData, orderBy, order]);

    const paginatedData = useMemo(() => {
        const start = page * rowsPerPage;
        return sortedData.slice(start, start + rowsPerPage);
    }, [sortedData, page, rowsPerPage]);

    return (
        <Paper
            elevation={2}
            sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 2,
                transition: 'all 0.2s ease-in-out',
            }}
        >
            {/* Table Header & Controls */}
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2,
                    mb: 2.5,
                }}
            >
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {t('common.breakdownTable', 'Monthly Billing Breakdown')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {t('common.showingResults', { count: filteredData.length })}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <TextField
                        size="small"
                        placeholder={t('common.filterByZone', 'Filter by month or status...')}
                        value={searchTerm}
                        onChange={handleSearchChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" color="action" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ minWidth: 220 }}
                    />

                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FileDownload />}
                        onClick={handleExport}
                        disabled={filteredData.length === 0}
                        sx={{ textTransform: 'none', height: 40, borderRadius: 1.5 }}
                    >
                        {t('common.exportCsv', 'Export CSV')}
                    </Button>
                </Box>
            </Box>

            {/* Table */}
            <TableContainer sx={{ maxHeight: 520, borderRadius: 1 }}>
                <Table stickyHeader size="small" aria-label="power cost breakdown table">
                    <TableHead>
                        <TableRow>
                            {/* Month */}
                            <TableCell sx={{ fontWeight: 700 }}>
                                <TableSortLabel
                                    active={orderBy === 'month'}
                                    direction={orderBy === 'month' ? order : 'asc'}
                                    onClick={() => handleSort('month')}
                                >
                                    {t('common.month', 'Month')}
                                </TableSortLabel>
                            </TableCell>

                            {/* Billing Period */}
                            <TableCell sx={{ fontWeight: 700 }}>
                                {t('common.billingPeriod', 'Billing Period')}
                            </TableCell>

                            {/* Consumption (kWh) */}
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                <TableSortLabel
                                    active={orderBy === 'totalKwh'}
                                    direction={orderBy === 'totalKwh' ? order : 'asc'}
                                    onClick={() => handleSort('totalKwh')}
                                >
                                    {t('common.unitsInKwh', 'Energy (kWh)')}
                                </TableSortLabel>
                            </TableCell>

                            {/* Base Energy Cost */}
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                <TableSortLabel
                                    active={orderBy === 'baseEnergyCost'}
                                    direction={orderBy === 'baseEnergyCost' ? order : 'asc'}
                                    onClick={() => handleSort('baseEnergyCost')}
                                >
                                    {t('common.baseEnergyCost', 'Base (฿)')}
                                </TableSortLabel>
                            </TableCell>

                            {/* Ft Cost */}
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                <TableSortLabel
                                    active={orderBy === 'ftCost'}
                                    direction={orderBy === 'ftCost' ? order : 'asc'}
                                    onClick={() => handleSort('ftCost')}
                                >
                                    {t('common.ftCost', 'Ft (฿)')}
                                </TableSortLabel>
                            </TableCell>

                            {/* VAT */}
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                {t('common.vatAmount', 'VAT (฿)')}
                            </TableCell>

                            {/* Total Cost */}
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                <TableSortLabel
                                    active={orderBy === 'totalCost'}
                                    direction={orderBy === 'totalCost' ? order : 'asc'}
                                    onClick={() => handleSort('totalCost')}
                                >
                                    {t('common.totalCost', 'Total (฿)')}
                                </TableSortLabel>
                            </TableCell>

                            {/* Status */}
                            <TableCell align="center" sx={{ fontWeight: 700 }}>
                                {t('common.status', 'Status')}
                            </TableCell>

                            {/* Actions */}
                            <TableCell align="center" sx={{ fontWeight: 700 }}>
                                {t('common.actions', 'Action')}
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {paginatedData.length > 0 ? (
                            paginatedData.map((row) => {
                                const isSelected = selectedMonth === row.month;
                                const isPartial = Boolean(row.isPartial);

                                return (
                                    <TableRow
                                        hover
                                        key={`${row.year}-${row.month}`}
                                        selected={isSelected}
                                        sx={{
                                            cursor: 'pointer',
                                            '&:nth-of-type(odd)': { bgcolor: isSelected ? undefined : 'action.hover' },
                                        }}
                                        onClick={() => onSelectMonth && onSelectMonth(row.month)}
                                    >
                                        {/* Month */}
                                        <TableCell sx={{ fontWeight: 700 }}>
                                            {getMonthName(row.month, t)} {row.year}
                                        </TableCell>

                                        {/* Billing Period */}
                                        <TableCell sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                                            {formatBillingPeriod(row.periodStart, row.periodEnd)}
                                        </TableCell>

                                        {/* Total kWh */}
                                        <TableCell align="right" sx={{ fontWeight: 600, color: '#3b82f6' }}>
                                            {formatKwh(row.totalKwh)}
                                        </TableCell>

                                        {/* Base Energy Cost */}
                                        <TableCell align="right">
                                            {formatCurrency(row.baseEnergyCost)}
                                        </TableCell>

                                        {/* Ft Cost */}
                                        <TableCell align="right">
                                            {formatCurrency(row.ftCost)}
                                        </TableCell>

                                        {/* VAT Amount */}
                                        <TableCell align="right">
                                            {formatCurrency(row.vatAmount)}
                                        </TableCell>

                                        {/* Total Cost */}
                                        <TableCell align="right" sx={{ fontWeight: 800, color: '#10b981' }}>
                                            {formatCurrency(row.totalCost)}
                                        </TableCell>

                                        {/* Status */}
                                        <TableCell align="center">
                                            <Chip
                                                icon={isPartial ? <HourglassTop fontSize="small" /> : <CheckCircle fontSize="small" />}
                                                label={isPartial ? t('common.cyclePartial', 'In Progress') : t('common.cycleCompleted', 'Completed')}
                                                size="small"
                                                color={isPartial ? 'warning' : 'success'}
                                                sx={{ height: 22, fontSize: '0.725rem', fontWeight: 600 }}
                                            />
                                        </TableCell>

                                        {/* View Receipt Action */}
                                        <TableCell align="center">
                                            <Tooltip title={t('common.viewReceipt', 'View Bill Receipt')}>
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (onSelectMonth) onSelectMonth(row.month);
                                                    }}
                                                >
                                                    <Receipt fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('common.noData', 'No billing data available')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Paper>
    );
}

export default PowerCostBreakdownTable;
